import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import PageHeader from '@/components/common/PageHeader';
import PageContainer from '@/components/common/PageContainer';
import { Button } from '@/components/ui/button';
import { Plus, Users, CreditCard, Settings, TrendingUp, Building, Calendar, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthContext } from '@/contexts/AuthContext';
import { formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Interface para os dados de família de assinaturas
interface SubscriptionFamilyData {
  name: string;
  count: number;
  color: string;
}

// Interface para assinaturas que terminam em breve
interface ExpiringSubscription {
  id: string;
  customer_name: string;
  tenant_name: string;
  end_date: string;
  days_remaining: number;
}

// Interface para novas assinaturas
interface NewSubscription {
  id: string;
  customer_name: string;
  tenant_name: string;
  start_date: string;
  plan_name: string;
  amount: number;
  customer_domain?: string;
}

// Função auxiliar para obter as iniciais do nome
const getInitials = (name: string) => {
  if (!name) return 'C';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const Index = () => {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    customers: 0,
    subscriptions: 0,
    mrr: 0,
    tenants: 0,
    plans: 0
  });
  const [subscriptionFamilies, setSubscriptionFamilies] = useState<SubscriptionFamilyData[]>([]);
  const [expiringSubscriptions, setExpiringSubscriptions] = useState<ExpiringSubscription[]>([]);
  const [newSubscriptions, setNewSubscriptions] = useState<NewSubscription[]>([]);

  // Cores para o gráfico
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f43f5e', '#84cc16'];

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch customer count
      const { count: customerCount, error: customerError } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true });
      
      if (customerError) throw customerError;
      
      // Fetch active subscriptions count
      const { count: subscriptionCount, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('state', 'active');
      
      if (subscriptionError) throw subscriptionError;
      
      // Fetch active subscription plans count
      const { count: planCount, error: planError } = await supabase
        .from('subscription_plans')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);
      
      if (planError) throw planError;
      
      // Fetch tenant count
      const { count: tenantCount, error: tenantError } = await supabase
        .from('customer_relationship')
        .select('*', { count: 'exact', head: true });
      
      if (tenantError) throw tenantError;
      
      // Calculate MRR (Monthly Recurring Revenue)
      const { data: subscriptionItems, error: mrrError } = await supabase
        .from('subscription_items')
        .select('unit_price, quantity')
        .eq('state', 'active');
      
      if (mrrError) throw mrrError;
      
      const mrr = subscriptionItems?.reduce((total, item) => {
        return total + (item.unit_price * item.quantity);
      }, 0) || 0;
      
      // Buscar dados de assinaturas por família
      const { data: familyData, error: familyError } = await supabase
        .from('subscription_plan_families')
        .select(`
          id,
          name,
          subscription_plans (
            id,
            subscription_items (
              id,
              subscription_id
            )
          )
        `);
      
      if (familyError) throw familyError;
      
      // Processar dados para o gráfico
      const processedFamilyData: SubscriptionFamilyData[] = familyData?.map((family, index) => {
        // Contar assinaturas únicas para cada família
        const subscriptionIds = new Set<string>();
        
        family.subscription_plans?.forEach(plan => {
          plan.subscription_items?.forEach(item => {
            if (item.subscription_id) {
              subscriptionIds.add(item.subscription_id);
            }
          });
        });
        
        return {
          name: family.name,
          count: subscriptionIds.size,
          color: colors[index % colors.length]
        };
      }).filter(family => family.count > 0) || [];
      
      // Ordenar por contagem (do maior para o menor)
      processedFamilyData.sort((a, b) => b.count - a.count);
      
      setSubscriptionFamilies(processedFamilyData);
      
      // Buscar assinaturas que terminam nos próximos 30 dias
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      
      const { data: expiringData, error: expiringError } = await supabase
        .from('subscriptions')
        .select(`
          id,
          end_date,
          customer_relationship:customer_relationship_id (
            id,
            customer:customer_id (
              id,
              name
            )
          )
        `)
        .eq('state', 'active')
        .gte('end_date', new Date().toISOString())
        .lte('end_date', thirtyDaysFromNow.toISOString())
        .order('end_date');
      
      if (expiringError) throw expiringError;
      
      // Processar dados de assinaturas que expiram
      const processedExpiringData: ExpiringSubscription[] = expiringData?.map(subscription => {
        const endDate = new Date(subscription.end_date);
        const today = new Date();
        const diffTime = Math.abs(endDate.getTime() - today.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return {
          id: subscription.id,
          customer_name: subscription.customer_relationship?.customer?.name || 'Cliente não encontrado',
          tenant_name: subscription.customer_relationship?.relationship || 'Tenant não encontrado',
          end_date: subscription.end_date,
          days_remaining: diffDays
        };
      }) || [];
      
      setExpiringSubscriptions(processedExpiringData);
      
      // Buscar novas assinaturas (últimos 30 dias)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: newSubsData, error: newSubsError } = await supabase
        .from('subscriptions')
        .select(`
          id,
          start_date,
          customer_relationship:customer_relationship_id (
            id,
            relationship,
            customer:customer_id (
              id,
              name,
              domain
            )
          ),
          subscription_items (
            id,
            unit_price,
            quantity,
            subscription_plan:subscription_plan_id (
              id,
              name
            )
          )
        `)
        .gte('start_date', thirtyDaysAgo.toISOString())
        .order('start_date', { ascending: false })
        .limit(5);
      
      if (newSubsError) throw newSubsError;
      
      // Processar dados de novas assinaturas
      const processedNewSubsData: NewSubscription[] = newSubsData?.map(subscription => {
        const items = subscription.subscription_items || [];
        
        // Calcular o valor total da assinatura
        const amount = items.reduce((total, item) => {
          return total + (item.unit_price * item.quantity);
        }, 0);
        
        // Obter o nome do primeiro plano (para simplificar)
        const planName = items[0]?.subscription_plan?.name || 'Plano não encontrado';
        
        return {
          id: subscription.id,
          customer_name: subscription.customer_relationship?.customer?.name || 'Cliente não encontrado',
          tenant_name: subscription.customer_relationship?.relationship || 'Tenant não encontrado',
          start_date: subscription.start_date,
          plan_name: planName,
          amount: amount,
          customer_domain: subscription.customer_relationship?.customer?.domain || null
        };
      }) || [];
      
      setNewSubscriptions(processedNewSubsData);
      
      setStats({
        customers: customerCount || 0,
        subscriptions: subscriptionCount || 0,
        mrr: mrr,
        tenants: tenantCount || 0,
        plans: planCount || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Falha ao carregar dados do painel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>CSM | Trivium - Dashboard</title>
      </Helmet>
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Clientes</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-extrabold">{stats.customers}</div>
                <p className="text-xs text-muted-foreground">Clientes cadastrados</p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Assinaturas</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-extrabold">{stats.subscriptions}</div>
                <p className="text-xs text-muted-foreground">Assinaturas ativas</p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Planos de Assinatura</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-extrabold">{stats.plans}</div>
                <p className="text-xs text-muted-foreground">Planos disponíveis</p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Tenants</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-extrabold">{stats.tenants}</div>
                <p className="text-xs text-muted-foreground">Tenants ativos</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts and Tables */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Receita Mensal Recorrente</CardTitle>
            <CardDescription>Visão geral da receita mensal recorrente</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-12 w-40" />
            ) : (
              <>
                <div className="text-3xl font-extrabold">{formatCurrency(stats.mrr)}</div>
                <p className="text-sm text-muted-foreground mt-2">Receita mensal recorrente total</p>
                <div className="h-[200px] w-full bg-muted/20 rounded-md mt-4 flex items-center justify-center">
                  <p className="text-muted-foreground">Gráfico de Receita</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Assinaturas por Família</CardTitle>
            <CardDescription>Distribuição de assinaturas por família de planos</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : subscriptionFamilies.length === 0 ? (
              <div className="h-[200px] w-full bg-muted/20 rounded-md flex items-center justify-center">
                <p className="text-muted-foreground">Nenhum dado disponível</p>
              </div>
            ) : (
              <>
                <div className="h-[200px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={subscriptionFamilies}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => [`${value} assinaturas`, 'Quantidade']}
                        labelFormatter={(label) => `Família: ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="count" name="Assinaturas" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {subscriptionFamilies.slice(0, 4).map((family, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: family.color }}></div>
                      <span className="text-sm">{family.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tables */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Assinaturas que terminam nos próximos 30 dias
            </CardTitle>
            <CardDescription>
              Lista de assinaturas que precisam de atenção
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : expiringSubscriptions.length === 0 ? (
              <div className="h-[100px] w-full bg-muted/20 rounded-md flex items-center justify-center">
                <p className="text-muted-foreground">Nenhuma assinatura expirando nos próximos 30 dias</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Data de Término</TableHead>
                      <TableHead>Dias Restantes</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiringSubscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell>{subscription.customer_name}</TableCell>
                        <TableCell>{subscription.tenant_name}</TableCell>
                        <TableCell>{new Date(subscription.end_date).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>
                          <Badge variant={subscription.days_remaining <= 7 ? "destructive" : "outline"}>
                            {subscription.days_remaining} dias
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/subscriptions/${subscription.id}`}>
                              Ver Detalhes
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Novas Assinaturas</CardTitle>
            <CardDescription>Novas assinaturas nos últimos 30 dias</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              Array(3).fill(0).map((_, index) => (
                <Skeleton key={index} className="h-[180px] w-full" />
              ))
            ) : newSubscriptions.length === 0 ? (
              <div className="col-span-full h-[100px] bg-muted/20 rounded-md flex items-center justify-center">
                <p className="text-muted-foreground">Nenhuma nova assinatura nos últimos 30 dias</p>
              </div>
            ) : (
              <div className="flex overflow-x-auto pb-4 space-x-4 snap-x">
                {newSubscriptions.map((subscription) => (
                  <Card key={subscription.id} className="flex-shrink-0 w-[280px] snap-center overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center space-y-4">
                        <Avatar className="h-16 w-16">
                          {subscription.customer_domain ? (
                            <AvatarImage 
                              src={`https://cdn.brandfetch.io/${subscription.customer_domain}?c=1idk4AUs2-buvooOUvQ`} 
                              alt={`Logo ${subscription.customer_name}`}
                            />
                          ) : (
                            <AvatarFallback className="text-lg">
                              {getInitials(subscription.customer_name)}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        
                        <div className="text-center space-y-1">
                          <h3 className="font-extrabold text-lg">{subscription.customer_name}</h3>
                          <p className="text-sm text-muted-foreground">{subscription.tenant_name}</p>
                        </div>
                        
                        <div className="w-full pt-4 space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Plano:</span>
                            <span className="text-sm font-extrabold">{subscription.plan_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Início:</span>
                            <span className="text-sm font-extrabold">{new Date(subscription.start_date).toLocaleDateString('pt-BR')}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="bg-muted/20 px-6 py-3">
                      <Button variant="outline" size="sm" className="w-full" asChild>
                        <Link to={`/subscriptions/${subscription.id}`}>
                          Ver Detalhes
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
};

export default Index;
